import { escapeHTML } from '../../utils/escape.js';
import { describeAsignacion } from '../../shared/mesas-policy.js';
import { RESERVATION_COLSPAN } from './reservationColumns.js';

/**
 * Fila de una reserva dentro del panel de staff. Función de render pura:
 * devuelve un string HTML, sin estado ni efectos. Todos los campos
 * provenientes de datos (nombre, apellido, area, momento, codigo, hora)
 * pasan por `escapeHTML` antes de interpolarse (Req: Reservation Row
 * Display).
 *
 * Product pivot (reservation_status_model_v3): el estado ya no es un badge
 * de solo-lectura + botones de acción separados. Ahora la celda Estado son 3
 * botones SEGMENTADOS (Confirmada / Cancelada / Terminada) donde el estado
 * ACTUAL de la fila es el ACTIVO (resaltado brand + texto blanco) y los otros
 * dos quedan atenuados/outline. Un click en uno no-activo cambia el estado de
 * inmediato (sin diálogo de confirmación); click en el activo es un no-op
 * inofensivo. La celda Acciones tiene un botón "Archivar" separado, mostrado
 * SOLO cuando el estado ∈ {cancelada, terminada}.
 *
 * Modo read-only (`{ readOnly: true }`): usado por la vista de archivadas
 * (lista plana). No renderiza botones de estado ni de archivar — solo una
 * etiqueta "Archivada" y un guion en Acciones.
 *
 * `ACTIONS_BY_STATUS` intencionalmente NO se importa desde
 * `admin-reservations.service.js` (que expone el mismo state machine como
 * `ACTIONS`) — un componente de render puro no debería depender del módulo
 * de service: los tests de wiring (`admin-reservations-wiring.test.js`)
 * `vi.mock` ese módulo COMPLETO (per el Testing Strategy de design.md,
 * "jsdom + mocked service"), así que un import real de `ACTIONS` rompería
 * ese mock. Esta es una duplicación consciente y acotada (literal del state
 * machine de spec.md, ya estable), no una segunda fuente de verdad de negocio.
 * `admin-reservations-state-machine.test.js` verifica que ambos mapas no se
 * desincronicen.
 */

const STATUS_LABELS = {
    confirmada: 'Confirmada',
    terminada: 'Terminada',
    cancelada: 'Cancelada',
    archivada: 'Archivada',
};

/**
 * Acciones lógicas válidas por estado actual (Req: Staff Status Transition
 * Actions / Archive Action Restricted to Cancelada or Terminada). Las 3
 * transiciones de estado (`confirmar`/`cancelar`/`terminar`) son libres entre
 * los 3 estados activos; `archivar` solo desde cancelada/terminada;
 * `archivada` es terminal ([]). Exportada (named export, sin efecto sobre el
 * render) para que `admin-reservations-state-machine.test.js` verifique que
 * este mapa y el `ACTIONS` del service no se desincronicen (R3-001).
 */
export const ACTIONS_BY_STATUS = {
    confirmada: ['confirmar', 'cancelar', 'terminar'],
    terminada: ['confirmar', 'cancelar', 'terminar', 'archivar'],
    cancelada: ['confirmar', 'cancelar', 'terminar', 'archivar'],
    archivada: [],
};

/** Los 3 estados seleccionables del segmentado, en orden de workflow. */
const SEGMENT_ESTADOS = [
    { estado: 'confirmada', label: 'Confirmada' },
    { estado: 'cancelada', label: 'Cancelada' },
    { estado: 'terminada', label: 'Terminada' },
];

/** Clases compartidas por cada celda del cuerpo de la tabla (densidad desktop). */
const CELL = 'px-3 py-3 align-middle text-sm text-text-primary';

/**
 * Renderiza el segmentado de 3 botones de estado. El estado actual es el
 * botón activo (brand + texto blanco); los otros dos quedan atenuados/outline.
 * @param {string} status
 * @param {string} safeId — id ya escapado
 * @returns {string}
 */
function renderEstadoSegments(status, safeId) {
    const buttons = SEGMENT_ESTADOS.map(({ estado, label }) => {
        const active = status === estado;
        // Activo: relleno brand sólido. Inactivo: transparente y atenuado, con
        // un hover teñido de brand que da feedback claro de que es clickeable.
        const classes = active
            ? 'bg-brand text-white shadow-sm'
            : 'bg-transparent text-text-muted hover:bg-brand/10 hover:text-brand';
        return `
                <button data-action="reserva-set-estado" data-estado="${estado}" data-id="${safeId}"
                    aria-pressed="${active}"
                    class="flex-1 min-w-0 px-1.5 py-1.5 text-center text-[9px] leading-none font-black uppercase tracking-tight whitespace-nowrap transition-colors ${classes}">
                    ${label}
                </button>
        `;
    }).join('');
    // Control segmentado connected: un solo contenedor con borde y separadores
    // internos (divide-x). `inline-flex` SIN wrap → los 3 segmentos viven
    // siempre en UNA fila, así la reserva ocupa una sola línea (regla del
    // rediseño: nada de filas altas).
    return `<div class="inline-flex w-full items-stretch rounded-lg border border-border-subtle overflow-hidden divide-x divide-border-subtle">${buttons}</div>`;
}

/**
 * Fila de reserva como `<tr>` de tabla (densidad desktop). Celdas: Hora,
 * Nombre (nombre + apellido, con el código como subtexto), Personas, Área,
 * Momento, Estado (segmentado o etiqueta read-only) y Acciones (archivar o —).
 *
 * @param {{ id: string, nombre: string, apellido: string, hora: string|null, personas: number, area: string, momento: string, codigo: string, status: string, comentarios?: string|null }} row
 * @param {{ readOnly?: boolean }} [options]
 * @returns {string} HTML de la fila (`<tr>`).
 */
export const ReservationRow = (row, { readOnly = false } = {}) => {
    const { id, nombre, apellido, hora, personas, area, momento, codigo, status, comentarios } = row;
    const comentario = String(comentarios ?? '').trim();
    const horaLabel = hora ? escapeHTML(hora) : 'Sin horario';
    const safeId = escapeHTML(id);
    const statusLabel = STATUS_LABELS[status] ?? escapeHTML(status);
    const mesasLabel = escapeHTML(describeAsignacion(row.mesas));
    const mesasVacias = !row.mesas || row.mesas.length === 0;

    // Estado: en read-only (archivadas) solo una etiqueta; si no, el segmentado.
    const estadoCell = readOnly
        ? `<span class="inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-surface border border-border-subtle text-text-muted opacity-60">${statusLabel}</span>`
        : renderEstadoSegments(status, safeId);

    // Archivar: solo desde cancelada/terminada, y nunca en read-only.
    const canArchivar = !readOnly && (status === 'cancelada' || status === 'terminada');
    const archivarBtnCell = canArchivar
        ? `<button data-action="reserva-archivar" data-id="${safeId}"
                class="px-3 py-1.5 rounded-lg border border-border-subtle text-[10px] font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors">
                Archivar
            </button>`
        : '';
    // Editar: abre el formulario completo (datos + mesas). Nunca en read-only:
    // una reserva archivada ya liberó sus mesas por trigger y no se toca.
    const editarBtn = readOnly
        ? ''
        : `<button data-action="reserva-editar" data-id="${safeId}"
                class="px-3 py-1.5 rounded-lg border border-border-subtle text-[10px] font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors">
                Editar
            </button>`;
    const acciones = [editarBtn, archivarBtnCell].filter(Boolean).join('');
    const accionesCell = acciones
        ? `<div class="flex items-center justify-end gap-2">${acciones}</div>`
        : '<span class="text-text-muted/50">—</span>';

    // table-fixed + `truncate` en las celdas de texto: el contenido largo se
    // recorta con elipsis dentro de su columna en lugar de generar scroll
    // horizontal (regla dura del rediseño full-width).
    // El comentario NO entra como octava columna: la tabla es `table-fixed` con
    // los anchos ya repartidos al 100%, así que una columna más aplastaría todo
    // y truncaría la nota justo cuando importa leerla. Va en una segunda `<tr>`
    // a lo ancho, y solo si hay algo escrito. Cuando aparece, ella se queda con
    // el borde inferior para que las dos filas se lean como una sola entrada.
    const comentarioRow = comentario
        ? `<tr data-reserva-comentario data-id="${safeId}" class="border-b border-border-subtle last:border-b-0 hover:bg-surface/40 transition-colors">
                <td colspan="${RESERVATION_COLSPAN}" class="px-3 pb-3 -mt-1">
                    <span class="flex items-start gap-2 text-[11px] leading-snug text-text-muted">
                        <i class="fa-solid fa-comment-dots text-brand/70 mt-0.5 shrink-0" aria-hidden="true"></i>
                        <span class="min-w-0">${escapeHTML(comentario)}</span>
                    </span>
                </td>
            </tr>`
        : '';

    return `
        <tr data-reserva-row data-id="${safeId}" class="${comentario ? '' : 'border-b border-border-subtle last:border-b-0'} hover:bg-surface/40 transition-colors">
            <td class="${CELL} font-bold truncate">${horaLabel}</td>
            <td class="${CELL} min-w-0">
                <span class="block font-bold truncate">${escapeHTML(nombre)} ${escapeHTML(apellido)}</span>
                <span class="block text-[10px] text-text-muted uppercase tracking-widest truncate">${escapeHTML(codigo)}</span>
            </td>
            <td class="${CELL} text-center truncate">${escapeHTML(String(personas))}</td>
            <td class="${CELL} min-w-0">
                <span class="block truncate">${escapeHTML(area)}</span>
                <span class="block text-[10px] ${mesasVacias ? 'text-text-muted/60 italic' : 'text-text-muted'} truncate">${mesasLabel}</span>
            </td>
            <td class="${CELL} truncate">${escapeHTML(momento)}</td>
            <td class="${CELL}">${estadoCell}</td>
            <td class="${CELL} text-right">${accionesCell}</td>
        </tr>
        ${comentarioRow}
    `;
};

/**
 * Tarjeta de reserva para MOBILE (`<div>`), alternativa a la fila de tabla: en
 * pantallas angostas una tabla de 7 columnas queda ilegible (todo aplastado y
 * truncado). La tarjeta apila los mismos datos —header (nombre + código + hora),
 * meta (personas · área · momento) y el control de estado a ancho COMPLETO (acá
 * el segmentado tiene lugar de sobra)— y es mucho más usable con el dedo.
 *
 * `ReservationGroup`/`ReservationsView` renderizan la tabla en `lg+` y estas
 * tarjetas en `<lg` (toggle por CSS `hidden lg:block` / `lg:hidden`). El wiring
 * de admin.js cablea por `[data-action]`/`data-id`, así que el `data-id`
 * duplicado (fila + tarjeta, una oculta por breakpoint) no genera conflicto:
 * se actúa sobre el elemento visible. Comparte `renderEstadoSegments` con la
 * fila para no duplicar la lógica del segmentado.
 *
 * @param {{ id: string, nombre: string, apellido: string, hora: string|null, personas: number, area: string, momento: string, codigo: string, status: string }} row
 * @param {{ readOnly?: boolean }} [options]
 * @returns {string} HTML de la tarjeta (`<div>`).
 */
export const ReservationCard = (row, { readOnly = false } = {}) => {
    const { id, nombre, apellido, hora, personas, area, momento, codigo, status, comentarios } = row;
    const comentario = String(comentarios ?? '').trim();
    const horaLabel = hora ? escapeHTML(hora) : 'Sin horario';
    const safeId = escapeHTML(id);
    const statusLabel = STATUS_LABELS[status] ?? escapeHTML(status);
    const mesasLabel = escapeHTML(describeAsignacion(row.mesas));
    const mesasVacias = !row.mesas || row.mesas.length === 0;

    // Estado: en read-only (archivadas) una etiqueta; si no, el segmentado
    // (a ancho completo dentro de la tarjeta).
    const estadoControl = readOnly
        ? `<span class="inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-base border border-border-subtle text-text-muted opacity-60">${statusLabel}</span>`
        : renderEstadoSegments(status, safeId);

    // Archivar: botón a ancho completo, solo desde cancelada/terminada. En la
    // tarjeta directamente lo omitimos si no aplica (no hace falta el guion).
    const canArchivar = !readOnly && (status === 'cancelada' || status === 'terminada');
    const archivarBtn = canArchivar
        ? `<button data-action="reserva-archivar" data-id="${safeId}"
                class="w-full py-2 rounded-lg border border-border-subtle text-[10px] font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors">
                Archivar
            </button>`
        : '';

    // Editar: mismo control que en la fila de escritorio.
    const editarBtn = readOnly
        ? ''
        : `<button data-action="reserva-editar" data-id="${safeId}"
                class="w-full py-2 rounded-lg border border-border-subtle text-[10px] font-black uppercase tracking-widest text-text-muted hover:text-brand transition-colors">
                Editar
            </button>`;

    return `
        <div data-reserva-row data-id="${safeId}" class="bg-surface border border-border-subtle rounded-2xl p-4 space-y-3">
            <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                    <span class="block font-bold text-text-primary truncate">${escapeHTML(nombre)} ${escapeHTML(apellido)}</span>
                    <span class="block text-[10px] text-text-muted uppercase tracking-widest truncate">${escapeHTML(codigo)}</span>
                </div>
                <span class="shrink-0 text-sm font-black text-text-primary">${horaLabel}</span>
            </div>
            <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-text-muted">
                <span class="font-bold text-text-primary">${escapeHTML(String(personas))} pers.</span>
                <span aria-hidden="true">·</span>
                <span>${escapeHTML(area)}</span>
                <span aria-hidden="true">·</span>
                <span>${escapeHTML(momento)}</span>
                <span aria-hidden="true">·</span>
                <span class="${mesasVacias ? 'text-text-muted/60 italic' : ''}">${mesasLabel}</span>
            </div>
            ${comentario
                ? `<p class="flex items-start gap-2 text-[11px] leading-snug text-text-muted">
                        <i class="fa-solid fa-comment-dots text-brand/70 mt-0.5 shrink-0" aria-hidden="true"></i>
                        <span class="min-w-0">${escapeHTML(comentario)}</span>
                    </p>`
                : ''}
            <div>${estadoControl}</div>
            ${editarBtn}
            ${archivarBtn}
        </div>
    `;
};
